import { useGetList, useTranslate } from "ra-core";
import { useMemo } from "react";

import { SelectInput } from "@/components/admin/select-input";
import { Card, CardContent } from "@/components/ui/card";

import { leadFormTypes } from "../contacts/leadModel";

/**
 * Cleans the raw form value into the shape capture_lead reads:
 * { form type: sales id }. Unassigned or invalid entries are dropped.
 */
export const normalizeLeadAssignments = (
  raw: Record<string, unknown> | undefined | null,
): Record<string, number> => {
  const result: Record<string, number> = {};
  for (const formType of leadFormTypes) {
    const value = raw?.[formType];
    const salesId = Number(value);
    if (
      value != null &&
      value !== "" &&
      Number.isInteger(salesId) &&
      salesId >= 0
    ) {
      result[formType] = salesId;
    }
  }
  return result;
};

/**
 * Builds form default values with every form type as an explicit key, so an
 * unassigned type resets to empty instead of keeping a stale value.
 */
export const toLeadAssignmentFormValues = (
  saved: Record<string, unknown> | undefined | null,
): Record<string, unknown> =>
  Object.fromEntries(leadFormTypes.map((t) => [t, saved?.[t] ?? ""]));

/** Settings card assigning an optional salesperson to each website form type. */
export const LeadAssignmentSettings = () => {
  const translate = useTranslate();
  const { data: sales } = useGetList("sales", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "last_name", order: "ASC" },
    filter: { "disabled@neq": true },
  });
  const choices = useMemo(
    () =>
      (sales ?? []).map((sale) => ({
        id: sale.id,
        name: `${sale.first_name} ${sale.last_name}`,
      })),
    [sales],
  );

  return (
    <Card id="lead-assignment">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.settings.lead_assignment.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("crm.settings.lead_assignment.help")}
        </p>
        {leadFormTypes.map((formType) => (
          <SelectInput
            key={formType}
            source={`leadAssignments.${formType}`}
            label={formType}
            choices={choices}
            helperText={false}
          />
        ))}
      </CardContent>
    </Card>
  );
};
